import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import { Alert, Button, Icon, Input, StatePanel, useToast } from "../../components/ui";

import "./inventory-locations-panel.scss";

const MAX_LOCATIONS = 50;

/**
 * Owner Admin → Inventory Locations (Alex, 2026-10-14): where the store keeps
 * its stock, offered as the optional "Inventory Location" dropdown on each
 * listing's store tab. Starts as In Store / With Client / On repair with
 * WatchMaker; rename, remove or add freely. Stored encrypted server-side.
 *
 * Rows keep their server id, so a rename follows through to every listing
 * that uses it. Saves on blur / Enter / remove — no Save button, like the
 * WhatsApp closing lines.
 */
export function InventoryLocationsPanel() {
  const toast = useToast();
  const [saved, setSaved] = useState(null); // last server state
  const [rows, setRows] = useState([]);
  const [save, setSave] = useState("idle"); // idle | saving | saved
  const [error, setError] = useState("");
  const savedTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    axios
      .get("/api/store/inventory-locations")
      .then(({ data }) => {
        if (!alive) return;
        setSaved(data.locations || []);
        setRows(data.locations || []);
      })
      .catch(() => alive && setError("Could not load inventory locations."));
    return () => {
      alive = false;
      clearTimeout(savedTimer.current);
    };
  }, []);

  const persist = async (next) => {
    const clean = next
      .map((r) => ({ id: r.id || null, label: (r.label || "").trim() }))
      .filter((r) => r.label);
    if (JSON.stringify(clean) === JSON.stringify(saved)) return;
    setSave("saving");
    setError("");
    try {
      const { data } = await axios.put("/api/store/inventory-locations", { locations: clean });
      setSaved(data.locations);
      // Keep any blank row the user just added; adopt server ids for the rest.
      setRows([...data.locations, ...next.filter((r) => !(r.label || "").trim())]);
      setSave("saved");
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSave("idle"), 2500);
      toast.success("Inventory locations saved");
    } catch (err) {
      setRows(saved);
      setSave("idle");
      setError(err?.response?.data?.message || "Couldn't save — try again.");
    }
  };

  if (!saved) {
    return error ? <Alert tone="error" title={error} /> : <StatePanel state="loading" loadingShape="row" compact />;
  }

  return (
    <div className="inv-locations-panel">
      <p className="inv-locations-panel__note">
        Where your stock physically is. Each listing can pick one of these on its store tab, so you always know which
        watch is where. Rename, remove or add as you need.
      </p>
      <ul className="inv-locations-panel__list">
        {rows.map((row, i) => (
          <li key={row.id || `new-${i}`} className="inv-locations-panel__row">
            <Input
              value={row.label}
              maxLength={100}
              placeholder="e.g. Locker 1 - London"
              aria-label={`Inventory location ${i + 1}`}
              onChange={(e) => setRows(rows.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)))}
              onBlur={() => persist(rows)}
              onKeyDown={(e) => e.key === "Enter" && persist(rows)}
              fullWidth
            />
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove inventory location ${i + 1}`}
              onClick={() => {
                const next = rows.filter((_, j) => j !== i);
                setRows(next);
                persist(next);
              }}
            >
              <Icon name="Trash2" size="sm" />
            </Button>
          </li>
        ))}
      </ul>
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<Icon name="Plus" size="sm" />}
        disabled={rows.length >= MAX_LOCATIONS}
        onClick={() => setRows([...rows, { id: null, label: "" }])}
      >
        Add a location
      </Button>
      <p className="inv-locations-panel__note">
        <Icon name="Lock" size="sm" /> This data is encrypted in the background.
      </p>
      <p className="inv-locations-panel__note" aria-live="polite">
        {save === "saving" ? "Saving…" : save === "saved" ? "All changes saved ✓" : ""}
      </p>
      {error && <Alert tone="error" title={error} />}
    </div>
  );
}

export default InventoryLocationsPanel;
